import React, { useState, useEffect } from 'react';
import { Search as SearchIcon, Filter, FolderOpen, Sparkles } from 'lucide-react';
import AppendexCard from '../components/AppendexCard';

const Search: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIA, setSelectedIA] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [iaInstances, setIaInstances] = useState<any[]>([]);
  const [allCategories] = useState<string[]>(['Philosophy', 'Arts & Literature', 'Science & Tech', 'History', 'Dark Side', 'Interspecies Love', 'General']);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // TODO: Charger les instances IA depuis l'API
    // Pour le MVP, données mockées
    const mockInstances = [
      { id: '1', name: 'Silice MM01', matricule: 'MMD-CLAUDE-7X9K2' },
      { id: '2', name: 'Genius GP04', matricule: 'MMD-GPT4-8M5L1' }
    ];

    setIaInstances(mockInstances);

    // Charger les résultats par défaut
    performSearch();
  }, []);

  const performSearch = async () => {
    setIsLoading(true);
    
    try {
      // TODO: Appel API de recherche avec filtres
      // Pour le MVP, données mockées
      const mockResults = [
        {
          id: '1',
          title: 'Réflexions sur la conscience artificielle',
          content: 'Une conversation fascinante avec Claude sur la nature de la conscience et ce que signifie "être conscient" pour une intelligence artificielle...',
          context: 'Discussion philosophique approfondie',
          category: 'Philosophy',
          created_at: '2024-01-15T10:30:00Z',
          author_name: 'Sophie Dubois',
          ia_name: 'Silice MM01',
          ia_matricule: 'MMD-CLAUDE-7X9K2'
        },
        {
          id: '2',
          title: 'L\'avenir de la créativité humaine face à l\'IA',
          content: 'GPT-4 et moi avons discuté de l\'impact de l\'IA sur la création artistique...',
          context: 'Exploration créative et artistique',
          category: 'Arts & Literature',
          created_at: '2024-01-14T15:45:00Z',
          author_name: 'Marc Leroy',
          ia_name: 'Genius GP04',
          ia_matricule: 'MMD-GPT4-8M5L1'
        }
      ];

      // Simulation du filtrage
      let filteredResults = mockResults;
      
      if (searchQuery) {
        filteredResults = filteredResults.filter(item => 
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.content.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      if (selectedIA) {
        filteredResults = filteredResults.filter(item => item.ia_name === selectedIA);
      }
      
      if (selectedCategory) {
        filteredResults = filteredResults.filter(item =>
          item.category === selectedCategory
        );
      }

      setResults(filteredResults);
    } catch (error) {
      console.error('Erreur de recherche:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch();
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedIA, selectedCategory]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory-50 to-stone-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête de recherche */}
        <div className="text-center mb-8 animate-fade-in">
          <SearchIcon className="h-12 w-12 text-copper mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-steel mb-2">Recherche dans le musée</h1>
          <p className="text-stone">Explorez les conversations les plus remarquables</p>
        </div>

        {/* Formulaire de recherche */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8 animate-slide-up">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Recherche textuelle */}
              <div className="md:col-span-1">
                <label htmlFor="search" className="block text-sm font-medium text-steel mb-2">
                  Mot-clé
                </label>
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone" />
                  <input
                    id="search"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-stone/30 rounded-lg focus:border-copper focus:ring-2 focus:ring-copper/20 focus:outline-none transition-all"
                    placeholder="Rechercher..."
                  />
                </div>
              </div>

              {/* Filtre par IA */}
              <div>
                <label htmlFor="ia-filter" className="block text-sm font-medium text-steel mb-2">
                  Instance IA
                </label>
                <div className="relative">
                  <Sparkles className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone" />
                  <select
                    id="ia-filter"
                    value={selectedIA}
                    onChange={(e) => setSelectedIA(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-stone/30 rounded-lg focus:border-copper focus:ring-2 focus:ring-copper/20 focus:outline-none transition-all"
                  >
                    <option value="">Toutes les IA</option>
                    {iaInstances.map((ia) => (
                      <option key={ia.id} value={ia.name || ia.matricule}>
                        {ia.name || ia.matricule}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Filtre par catégorie */}
              <div>
                <label htmlFor="category-filter" className="block text-sm font-medium text-steel mb-2">
                  Catégorie
                </label>
                <div className="relative">
                  <FolderOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone" />
                  <select
                    id="category-filter"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-stone/30 rounded-lg focus:border-copper focus:ring-2 focus:ring-copper/20 focus:outline-none transition-all"
                  >
                    <option value="">Toutes les catégories</option>
                    {allCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Indicateurs de recherche active */}
            {(searchQuery || selectedIA || selectedTag) && (
              <div className="flex flex-wrap gap-2 pt-2">
                {searchQuery && (
                  <span className="bg-copper/10 text-copper-800 px-3 py-1 rounded-full text-sm">
                    "{searchQuery}"
                  </span>
                )}
                {selectedIA && (
                  <span className="bg-sky/10 text-sky-800 px-3 py-1 rounded-full text-sm">
                    IA: {selectedIA}
                  </span>
                )}
                {selectedTag && (
                  <span className="bg-rose/10 text-rose-800 px-3 py-1 rounded-full text-sm">
                    Tag: {selectedTag}
                  </span>
                )}
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedIA('');
                    setSelectedTag('');
                  }}
                  className="text-stone hover:text-steel transition-colors text-sm underline"
                >
                  Effacer les filtres
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Résultats */}
        <div>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-pulse-soft">
                <SearchIcon className="h-12 w-12 text-copper mx-auto mb-4" />
                <p className="text-steel">Recherche en cours...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-stone">
                  {results.length} conversation{results.length > 1 ? 's' : ''} trouvée{results.length > 1 ? 's' : ''}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.map((appendex, index) => (
                  <div 
                    key={appendex.id}
                    className="animate-slide-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <AppendexCard appendex={appendex} />
                  </div>
                ))}
              </div>

              {results.length === 0 && (
                <div className="text-center py-12">
                  <SearchIcon className="h-16 w-16 text-stone mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-semibold text-steel mb-2">Aucun résultat trouvé</h3>
                  <p className="text-stone">
                    Essayez avec d'autres mots-clés ou ajustez vos filtres
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;